<template>
  <div>
    <Edit :editDialog="editDialog" @close="editDialog = false" @submit="getLead" :editLead="selectedLead"></Edit>
    <div v-if="lead" class="application-profile-page">
      <div class="profile-topbar">
        <v-btn text color="primary" to="/applications/list"><v-icon left>mdi-arrow-left</v-icon>Applications</v-btn>
        <div class="profile-navigation">
          <v-btn icon :disabled="!canMoveBack" aria-label="Previous application" @click="moveBack"><v-icon>mdi-chevron-left</v-icon></v-btn>
          <span>{{ navigationLabel }}</span>
          <v-btn icon :disabled="!canMoveNext" aria-label="Next application" @click="moveNext"><v-icon>mdi-chevron-right</v-icon></v-btn>
        </div>
      </div>

      <v-alert v-if="lead.alert" border="left" type="warning" text class="profile-alert"><strong>{{ lead.note }}</strong></v-alert>

      <v-card elevation="0" class="profile-hero">
        <div class="hero-main">
          <v-avatar size="52" color="green lighten-5"><span class="primary--text font-weight-bold">{{ initials(lead.fatherName) }}</span></v-avatar>
          <div class="hero-identity">
            <div class="page-eyebrow">Application #{{ applicationDisplayId }}</div>
            <h1>{{ lead.fatherName || "Application contact" }}</h1>
            <p>Submitted {{ lead.createdAt | moment("MMM D, YYYY") }} · {{ lead.LeadStudents.length }} {{ lead.LeadStudents.length === 1 ? "student" : "students" }}</p>
          </div>
        </div>
        <div class="hero-actions">
          <v-btn v-if="lead.CustomerId" outlined color="primary" @click="viewProfile(lead.CustomerId)"><v-icon left small>mdi-account-check-outline</v-icon>Parent profile</v-btn>
          <v-btn v-if="checkRightStatus(44)" outlined color="primary" @click="editLead(lead)"><v-icon left>mdi-pencil-outline</v-icon>Edit</v-btn>
          <v-menu bottom left offset-y>
            <template v-slot:activator="{ on, attrs }"><v-btn v-bind="attrs" v-on="on" icon outlined color="primary" aria-label="Application actions"><v-icon>mdi-dots-horizontal</v-icon></v-btn></template>
            <v-list class="menu-button">
              <v-list-item v-if="checkRightStatus(44)" @click="editLead(lead)"><v-list-item-title>Edit application</v-list-item-title></v-list-item>
              <v-list-item v-if="checkRightStatus(45)" @click="deleteLead(lead)"><v-list-item-title>Delete application</v-list-item-title></v-list-item>
            </v-list>
          </v-menu>
        </div>
      </v-card>
    <v-container grid-list-lg fluid class="profile-content">
      <div v-if="lead" class="information-grid">
        <v-card elevation="0" class="information-card">
          <div class="section-heading"><v-icon color="primary">mdi-account-group-outline</v-icon><div><h2>Parent information</h2><p>Primary family contacts</p></div></div>
          <div class="contact-grid">
            <div class="contact-person">
              <span class="contact-role">Father</span><strong>{{ lead.fatherName || "Not provided" }}</strong>
              <a v-if="lead.fatherPhoneNumber" :href="`tel:${lead.fatherPhoneNumber}`"><v-icon x-small>mdi-phone-outline</v-icon>{{ lead.fatherPhoneNumber | USPhoneNumber }}</a>
              <a v-if="lead.fatherEmailAddress" @click="sendEmail(lead.fatherEmailAddress)"><v-icon x-small>mdi-email-outline</v-icon>{{ lead.fatherEmailAddress }}</a>
            </div>
            <div class="contact-person">
              <span class="contact-role">Mother</span><strong>{{ lead.motherName || "Not provided" }}</strong>
              <a v-if="lead.motherPhoneNumber" :href="`tel:${lead.motherPhoneNumber}`"><v-icon x-small>mdi-phone-outline</v-icon>{{ lead.motherPhoneNumber | USPhoneNumber }}</a>
              <a v-if="lead.motherEmailAddress" @click="sendEmail(lead.motherEmailAddress)"><v-icon x-small>mdi-email-outline</v-icon>{{ lead.motherEmailAddress }}</a>
            </div>
          </div>
        </v-card>

        <v-card elevation="0" class="information-card">
          <div class="section-heading"><v-icon color="primary">mdi-map-marker-outline</v-icon><div><h2>Home address</h2><p>Applicant residence</p></div></div>
          <button v-if="lead.address" type="button" class="address-button" @click="openGoogleMap">
            <strong>{{ lead.address }}</strong><span>{{ lead.city }}, {{ lead.state }} {{ lead.zip }}</span>
            <small>Open in Google Maps <v-icon x-small>mdi-open-in-new</v-icon></small>
          </button>
          <div v-else class="empty-value">No address provided</div>
        </v-card>
      </div>

      <v-layout v-if="lead && lead.emergencyName">
        <v-flex>
          <v-card elevation="0" class="information-card emergency-card">
            <div class="section-heading"><v-icon color="primary">mdi-phone-alert-outline</v-icon><div><h2>Emergency contact</h2><p>Contact if a parent cannot be reached</p></div></div>
            <v-card-text class="pt-0">
              <v-row>
                <v-col>
                  <div class="field-label">Name</div>
                  <div class="text-capitalize font-weight-medium">{{ lead.emergencyName }}</div>
                </v-col>
                <v-col>
                  <div class="field-label">Phone</div>
                  <div class="text-capitalize font-weight-medium">{{ lead.emergencyPhoneNumber | USPhoneNumber }}</div>
                </v-col>
                <v-col>
                  <div class="field-label">Relationship</div>
                  <div class="text-capitalize font-weight-medium">{{ lead.emergencyRelation }}</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
      <v-layout row wrap v-if="lead && lead.LeadStudents.length !== 0">
        <v-flex xs12>
          <v-card elevation="0" class="students-card">
            <div class="students-heading"><div><h2>Students</h2><p>Applicants included in this family application</p></div>
              <v-btn v-if="lead.isChild" color="primary" outlined small @click="openAddStudentDialog"><v-icon left small>mdi-plus</v-icon>Add student</v-btn></div>
            <v-card-text class="pa-0">
              <v-data-table :headers="headers" hide-default-footer :items="lead.LeadStudents" :loading="loading"
                :expanded.sync="expanded" show-expand :items-per-page="20">
                <template v-slot:item="{ item, isExpanded, expand }">
                  <tr class="pointer">
                    <td class="text-left">
                      <v-btn @click="expand(true)" icon v-if="!isExpanded"><v-icon>mdi-chevron-down</v-icon></v-btn>
                      <v-btn @click="expand(false)" icon v-if="isExpanded"><v-icon>mdi-chevron-up</v-icon></v-btn>
                    </td>
                    <td class="text-left application-student-id">#{{ item.localId }}</td>
                    <td class="text-left text-capitalize">
                      {{ item.fullName }}
                    </td>
                    <td>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <span v-on="on">
                            {{ getAge(item.dob) }}</span>
                        </template>
                        <span> {{ item.dob | moment("L") }}</span>
                      </v-tooltip>
                    </td>
                    <td class="text-left">
                      <span v-if="item.Class">{{ item.Class.name }}</span>
                      <span v-else>Not Selected</span>
                    </td>
                    <td>
                      <div>
                        <v-menu offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn :loading="statusLoading" small outlined color="primary" v-bind="attrs" v-on="on">
                              {{ item.status }} <v-icon right small>mdi-chevron-down</v-icon>
                            </v-btn>
                          </template>

                          <v-list>
                            <v-list-item @click="changeStatus(status, item)" v-for="(status, index) in leadStatus"
                              :key="index">
                              <v-list-item-title>{{ status }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>

                    </td>
                    <td class="justify-end align-center text-right">
                      <v-menu bottom left offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn v-bind="attrs" v-on="on" icon>
                            <v-icon>mdi-dots-horizontal</v-icon>
                          </v-btn>
                        </template>
                        <v-list class="menu-button">

                          <v-list-item @click="editStudent(item)">
                            <v-list-item-title>Edit</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="deleteStudent(item)">
                            <v-list-item-title>Delete</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </td>
                  </tr>
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length" class="pa-0">
                    <v-card outlined class="ma-3 metadata-card">
                      <v-card-text>
                        <v-row>
                          <v-col cols="6" v-for="(detail, idx) in item.metadata" :key="idx">
                            <h2 class="text-capitalize font-weight-regular subtitle-2">Q: {{ detail.question }}</h2>
                            <div v-if="detail.selectedAnswerType === 'Checkboxes'">
                              <v-layout row wrap>
                                <v-flex shrink class="pr-0">
                                  A:
                                </v-flex>
                                <v-flex class="pl-0">
                                  <div v-if="detail && detail.ChoiceList">
                                    <div v-for="(list, index) in detail.ChoiceList" :key="index"
                                      class="font-weight-bold text-capitalize">
                                      <v-checkbox readonly v-model="list.answer" :key="index" :label="list.label"
                                        hide-details class="mt-0 shrink mx-0"></v-checkbox>
                                    </div>
                                  </div>
                                </v-flex>
                              </v-layout>
                            </div>
                            <div v-if="detail.selectedAnswerType === 'Multiple choice'">
                              <v-layout row wrap>
                                <v-flex shrink class="pr-0">
                                  A:
                                </v-flex>
                                <v-flex class="pl-0">
                                  <v-radio-group readonly v-model="detail.answer" v-if="detail && detail.ChoiceList">
                                    <v-radio v-for="(list, ind) in detail.ChoiceList" v-model="list.answer" :key="ind"
                                      :label="list.label" hide-details class="mt-0 shrink mx-0"
                                      :value="list.answer"></v-radio>
                                  </v-radio-group>
                                </v-flex>
                              </v-layout>
                            </div>
                            <p v-if="detail.selectedAnswerType === 'Short answer'"
                              class="font-weight-bold text-capitalize">A: {{ detail.answer }}</p>
                            <p v-if="detail.selectedAnswerType === 'Dropdown'"
                              class="font-weight-bold text-capitalize">A: {{ detail.answer }}</p>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </td>
                </template>
                <!-- <v-alert slot="no-results" :value="true" color="error" icon="warning">Your search for "{{ search }}" found
              no results.</v-alert> -->

              </v-data-table>
            </v-card-text>
          </v-card>

        </v-flex>
      </v-layout>
      <v-layout row wrap>
      
        <v-flex xs12 v-if="lead" class="comment-section">
          <Comment :RelationId="lead" :TypeId="2" />
        </v-flex>
      </v-layout>
    </v-container>
    </div>
    
    <v-dialog v-model="editStudentDialog" persistent max-width="620">
      <v-card v-if="Student" v-draggable-dialog class="student-dialog">
        <div class="dialog-drag-handle student-dialog-header">
          <div class="d-flex align-center"><v-avatar size="40" color="green lighten-5" class="mr-3"><v-icon color="primary">mdi-account-edit-outline</v-icon></v-avatar>
            <div><div class="text-h6 font-weight-bold dialog-title">Edit student</div><div class="caption text--secondary">Update student and requested class information.</div></div></div>
          <div class="d-flex align-center"><v-icon small color="grey lighten-1" class="mr-2 drag-indicator">mdi-drag</v-icon>
            <v-btn icon aria-label="Close edit student" @click.stop="closeEditDialog"><v-icon>mdi-close</v-icon></v-btn></div>
        </div>
        <v-divider />
        <v-card-text class="student-dialog-body">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field outlined dense :error-messages="studentFirstNameErrors" v-model="Student.firstName" label="First name" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field outlined dense :error-messages="studentLastNameErrors" v-model="Student.lastName" label="Last name" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select dense outlined :items="['male', 'female']" :error-messages="genderErrors" v-model="Student.gender" label="Gender" class="text-capitalize" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field outlined dense type="date" :error-messages="dobError" v-model="Student.dob" label="Date of birth" />
            </v-col>
            <v-col cols="12">
              <v-autocomplete dense outlined :items="Classes" clearable item-text="name" :error-messages="classIdErrors"
                v-model="Student.selectedClass" return-object label="Requested class">
                <template slot="item" slot-scope="{ item }">
                  <span>{{ item.name }}</span><v-spacer /><strong>{{ item.defaultFee | currency }}</strong>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <div v-if="Student.dob" class="student-age-summary" :class="{ 'student-age-summary--warning': isBelowMinimumAge }">
            <v-icon small :color="isBelowMinimumAge ? 'orange darken-2' : 'primary'">{{ isBelowMinimumAge ? 'mdi-alert-outline' : 'mdi-check-circle-outline' }}</v-icon>
            <span v-if="isBelowMinimumAge">{{ studentDisplayName }} is {{ studentAge }} years old. Minimum admission age is 5.</span>
            <span v-else>{{ studentDisplayName }} is {{ studentAge }} years old.</span>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="student-dialog-actions">
          <v-btn text @click="closeEditDialog">Cancel</v-btn>
          <v-btn color="primary" depressed :disabled="!Student.dob || isBelowMinimumAge" @click="saveStudent">
            <v-icon left small>mdi-content-save-outline</v-icon>Save changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="addStudentDialog" persistent max-width="620">
      <v-card v-if="Student" v-draggable-dialog class="student-dialog">
        <div class="dialog-drag-handle student-dialog-header">
          <div class="d-flex align-center"><v-avatar size="40" color="green lighten-5" class="mr-3"><v-icon color="primary">mdi-account-plus-outline</v-icon></v-avatar>
            <div><div class="text-h6 font-weight-bold dialog-title">Add student</div><div class="caption text--secondary">Add another student to this family application.</div></div></div>
          <div class="d-flex align-center"><v-icon small color="grey lighten-1" class="mr-2 drag-indicator">mdi-drag</v-icon>
            <v-btn icon aria-label="Close add student" @click.stop="closeAddStudentDialog"><v-icon>mdi-close</v-icon></v-btn></div>
        </div>
        <v-divider />
        <v-card-text class="student-dialog-body">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field outlined dense :error-messages="studentFirstNameErrors" autofocus v-model="Student.firstName" label="First name" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field outlined dense :error-messages="studentLastNameErrors" v-model="Student.lastName" label="Last name" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select dense outlined :items="['male', 'female']" :error-messages="genderErrors" v-model="Student.gender" label="Gender" class="text-capitalize" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field outlined dense type="date" :error-messages="dobError" v-model="Student.dob" label="Date of birth" />
            </v-col>
            <v-col cols="12">
              <v-autocomplete dense outlined :items="Classes" clearable item-text="name" :error-messages="classIdErrors"
                v-model="Student.selectedClass" return-object label="Requested class">
                <template slot="item" slot-scope="{ item }">
                  <span>{{ item.name }}</span><v-spacer /><strong>{{ item.defaultFee | currency }}</strong>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <div v-if="Student.dob" class="student-age-summary" :class="{ 'student-age-summary--warning': isBelowMinimumAge }">
            <v-icon small :color="isBelowMinimumAge ? 'orange darken-2' : 'primary'">{{ isBelowMinimumAge ? 'mdi-alert-outline' : 'mdi-check-circle-outline' }}</v-icon>
            <span v-if="isBelowMinimumAge">{{ studentDisplayName }} is {{ studentAge }} years old. Minimum admission age is 5.</span>
            <span v-else>{{ studentDisplayName }} is {{ studentAge }} years old.</span>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="student-dialog-actions">
          <v-btn text @click="closeAddStudentDialog">Cancel</v-btn>
          <v-btn color="primary" depressed :disabled="!Student.dob || isBelowMinimumAge" @click="addStudent">
            <v-icon left small>mdi-account-plus-outline</v-icon>Add student
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import applicationService from "../service";
import Comment from "@/modules/Comment/components/List";
import { mapGetters } from "vuex";
const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");
const Edit = () => import("../components/Edit");
import GlobalFunction from "@/mixins/GlobalFunction";
import classService from "@/modules/Classes/service.js";

export default {
  name: "laed-profile",
  data() {
    return {
      expanded: [],
      singleExpand: false,
      addStudentDialog: false,
      Student: {
        firstName: null,
        lastName: null,
        dob: null,
        selectedClass: null,
        ClassId: null,
      },
      Classes: [],
      editStudentDialog: false,
      leadStatus: ["Open", "Contacted", "Waiting", "Closed", "Ready to join", "Convert"],
      loading: false,
      statusLoading: false,
      editDialog: false,
      lead: null,
      selectedLead: null,
      note: null,
      applicationQueue: [],
      applicationCurrentIndex: -1,
      headers: [
        {
          text: 'ID',
          align: "left",
          value: "localId",
          sortable: true,
        }, {
          text: 'Name',
          align: "left",
          value: "fullName",
          sortable: true,
        },
        {
          text: 'Age',
          align: "left",
          value: "name",
          sortable: true,
        }, {
          text: 'Class Name',
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: 'Status',
          align: "left",
          value: "name",
          sortable: false,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        }
      ],
    };
  },
  created() {
    this.loadApplicationQueue();
    this.getLead();
    this.$events.listen("applicationProfileEvent", () => {
      this.getLead();
    });
  },
  beforeDestroy() {
    this.$events.remove("applicationProfileEvent");
  },
  watch: {
    "$route.params.id"(next, previous) {
      if (next && next !== previous) this.getLead();
    },
  },
  // watch: {
  //   editStudentDialog(val) {
  //     if (val) {
  //       this.getClassList()
  //     }
  //   }
  // },
  components: {
    Comment,
    Edit,
  },
  mixins: [validationMixin, GlobalFunction],
  validations: {
    Student: {
      firstName: {
        required,
      },
      lastName: {
        required,
      },
      dob: {
        required,
      },
      gender: {
        required,
      },
      selectedClass: {
        required,
      },
    },
  },
  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
    canMoveBack() {
      return this.applicationCurrentIndex > 0;
    },
    canMoveNext() {
      return this.applicationCurrentIndex >= 0 && this.applicationCurrentIndex < this.applicationQueue.length - 1;
    },
    navigationLabel() {
      return `Application #${this.applicationDisplayId}`;
    },
    applicationDisplayId() {
      if (!this.lead) return "—";
      if (this.lead.localId) return this.lead.localId;
      if (this.lead.LeadStudents && this.lead.LeadStudents.length && this.lead.LeadStudents[0].localId) {
        return this.lead.LeadStudents[0].localId;
      }
      return this.lead.id || "—";
    },
    studentAge() {
      return this.Student && this.Student.dob ? this.getAge(this.Student.dob) : null;
    },
    isBelowMinimumAge() {
      return this.studentAge !== null && this.studentAge < 5;
    },
    studentDisplayName() {
      const name = `${this.Student.firstName || ""} ${this.Student.lastName || ""}`.trim();
      return name || "This student";
    },
    genderErrors() {
      const errors = [];
      if (!this.$v.Student.gender.$dirty) return errors;
      if (!this.$v.Student.gender.required) errors.push("Gender is required");

      return errors;
    },
    studentFirstNameErrors() {
      const errors = [];
      if (!this.$v.Student.firstName.$dirty) return errors;
      if (!this.$v.Student.firstName.required)
        errors.push("First Name is required");
      return errors;
    },
    studentLastNameErrors() {
      const errors = [];
      if (!this.$v.Student.lastName.$dirty) return errors;
      if (!this.$v.Student.lastName.required)
        errors.push("Last Name is required");
      return errors;
    },
    dobError() {
      const errors = [];
      if (!this.$v.Student.dob.$dirty) return errors;
      if (!this.$v.Student.dob.required)
        errors.push("Date of birth is required");
      return errors;
    },
    classIdErrors() {
      const errors = [];
      if (!this.$v.Student.selectedClass.$dirty) return errors;
      if (!this.$v.Student.selectedClass.required)
        errors.push("Class is required");
      // if (!this.$v.Product.selectedClass.minValue) errors.push("Please Select Department");

      return errors;
    },
  },
  methods: {
    initials(name) {
      return (name || "A").split(" ").filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase();
    },
    loadApplicationQueue() {
      try {
        this.applicationQueue = JSON.parse(sessionStorage.getItem("applicationProfileQueue")) || [];
      } catch (error) {
        this.applicationQueue = [];
      }
      this.setApplicationIndex();
    },
    setApplicationIndex() {
      this.applicationCurrentIndex = this.applicationQueue.findIndex(
        item => String(item.id) === String(this.$route.params.id)
      );
    },
    navigateApplication(index) {
      if (index < 0 || index >= this.applicationQueue.length) return;
      const application = this.applicationQueue[index];
      this.applicationCurrentIndex = index;
      this.$router.push({ path: `/applications/${application.id}/profile` });
    },
    moveBack() {
      this.navigateApplication(this.applicationCurrentIndex - 1);
    },
    moveNext() {
      this.navigateApplication(this.applicationCurrentIndex + 1);
    },
    openAddStudentDialog() {
      this.getClassList()
      this.addStudentDialog = !this.addStudentDialog
    },
    addStudent() {
      console.log('student added')
      this.$v.Student.$touch();
      if (this.$v.Student.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });

      } else {
        this.Student.LeadId = this.lead.id
        if (this.Student && this.Student.selectedClass) {
          this.Student.ClassId = this.Student.selectedClass.id
        }
        applicationService.addLeadStudent(this.Student)
          .then(result => {
            console.log('update result', result)
            this.emptyStudent()
            this.addStudentDialog = false
            this.getLead();
          })
      }

    },
    saveStudent() {
      console.log('item', this.Student)
      this.$v.Student.$touch();
      if (this.$v.Student.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
        return false
      } else {
        this.Student.ClassId = this.Student.selectedClass.id
        applicationService.updateLeadStudent(this.Student.id, this.Student)
          .then(result => {
            console.log('update result', result)
            this.emptyStudent()
            this.editStudentDialog = false
            this.getLead();
          })
      }

    },
    getClassList() {
      return classService.getAll().then((response) => {
        console.log("response", response);
        this.loading = false;
        this.Classes = response.data;
        this.Classes.map(row => {
          if (row.id === this.Student.ClassId) {
            this.Student.selectedClass = row
          }
        })
        return response;
      });
    },
    emptyStudent() {
      this.$v.Student.$reset();
      this.Student = {
        firstName: null,
        lastName: null,
        dob: null,
        selectedClass: null,
        ClassId: null,
      }
    },
    closeEditDialog() {
      this.emptyStudent()
      this.editStudentDialog = false
    },
    closeAddStudentDialog() {
      this.emptyStudent()
      this.addStudentDialog = false
    },
    editStudent(item) {
      this.getClassList()
      this.editStudentDialog = !this.editStudentDialog
      this.Student = JSON.parse(JSON.stringify(item))
      this.Student.dob = this.Student.dob.split("T")[0]
      // this.Student.dob = bDate[0]
      // console.log('dob', bDate)
      // console.log('dob', dobDate.getDay())
      console.log('item', this.Student)
    },
    viewProfile(id) {
      this.$router.push({ path: `/parents/${id}/profile` });
    },
    viewProfileInNewTab(id) {
      let routeData = this.$router.resolve({
        path: `/parents/${id}/profile`,
      });
      window.open(routeData.href, "_blank");
    },
    statusColor(status) {
      let color = 'green'
      switch (status) {
        case 'Open':
          color = '#1976D2'
          break;
        case 'Contacted':
          color = '#6A1B9A'
          break;
        case 'Closed':
          color = 'red'
          break;
        case 'Waiting':
          color = '#AED581'
          break;

        case 'Convert':
          color = 'green'
          break;
        case 'Ready to join':
          color = '#00BFA5'
          break;
        default:
          color = 'green'
      }

      return color
    },
    changeStatus(status, studentLead) {
      // this.statusLoading = true
      console.log('val', status, studentLead)
      if (status === 'Convert') {
        this.$swal({
          title: "Are you sure?",
          text: `You want to convert ${studentLead.fullName}. You won't be able to revert this!`,
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#B71C1C",
          cancelButtonColor: "#43A047",
          cancelButtonText: "No",
          confirmButtonText: "Yes",
        }).then((result) => {
          if (result.value) {
            if (studentLead.status !== status) {
              this.convertStudentLead(studentLead)
            }
          }
        });
      } else {
        if (studentLead.status !== status) {
          applicationService.leadStudentUpdate(studentLead.id, { status: status })
            .then(result => {
              console.log('lead update', result)
              this.statusLoading = false
              studentLead.status = status
            })
        }
      }


    },
    convertStudentLead(studentLead, payload = {}) {
      return applicationService.convertLead(studentLead.id, payload)
        .then(result => {
          console.log('lead update', result)
          this.statusLoading = false
          studentLead.status = 'Convert'
          this.getLead()
        }).catch(err => {
          const response = err && err.data ? err.data : {}
          const candidates = response.existingCustomers

          if (response.existingParentConfirmationRequired && Array.isArray(candidates) && candidates.length) {
            return this.confirmExistingParent(candidates).then(customer => {
              if (!customer) return

              return this.convertStudentLead(studentLead, {
                existingCustomerId: customer.id,
                confirmExistingCustomer: true,
              })
            })
          }

          console.log('err', err)
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: response.message || "Unable to convert student lead",
            });
        })
    },
    confirmExistingParent(candidates) {
      const parentLabel = customer => {
        const contact = [customer.email, customer.phoneNumber].filter(Boolean).join(' | ')
        return contact ? `${customer.name || 'Existing parent'} (${contact})` : customer.name || 'Existing parent'
      }

      if (candidates.length === 1) {
        const customer = candidates[0]
        return this.$swal({
          title: "Existing parent found",
          text: `Is this the parent? ${parentLabel(customer)}`,
          icon: "question",
          showCancelButton: true,
          cancelButtonText: "No",
          confirmButtonText: "Yes, add student",
        }).then(result => result.value ? customer : null)
      }

      const inputOptions = candidates.reduce((options, customer) => {
        options[customer.id] = parentLabel(customer)
        return options
      }, {})

      return this.$swal({
        title: "Select the existing parent",
        text: "More than one parent matches this application.",
        icon: "question",
        input: "select",
        inputOptions,
        inputPlaceholder: "Select a parent",
        inputValidator: value => value ? null : "Please select a parent",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        confirmButtonText: "Add student",
      }).then(result => {
        if (!result.value) return null
        return candidates.find(customer => String(customer.id) === String(result.value)) || null
      })
    },
    getAge(dateString) {
      var today = new Date();
      var birthDate = new Date(dateString);
      var age = today.getFullYear() - birthDate.getFullYear();
      var m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    },
    updateAlert(val) {
      let data = {
        alert: val.value,
      };
      this.updateLead(data);
    },
    openGoogleMap() {
      window.open(
        `https://www.google.com/maps/place/${this.lead.address} ${this.lead.city},${this.lead.state} ${this.lead.zip}`,
        "_blank"
      );
    },
    updateNote() {
      let data = {
        note: this.note,
      };
      this.updateLead(data);
    },
    updateLead(data) {
      return applicationService.update(this.lead.id, data).then(() => {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "success",
            title: "Update Successfully",
          });
        this.getLead();
      });
    },
    editLead(lead) {
      this.selectedLead = lead;
      this.editDialog = !this.editDialog;
    },
    deleteStudent(student) {
      console.log('studne', student)
      this.$swal({
        title: "Are you sure?",
        text: `You want to remove ${student.fullName} from this application?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          applicationService.deleteLeadStudent(student.id).then((response) => {
            if (response.data) {
              this.$swal("Deleted!", "Student has been deleted.", "success");
              this.getLead()
            }
          });
        }
      });
    },
    deleteLead(lead) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          applicationService.delete(lead.id).then((response) => {
            if (response.data) {
              this.$swal("Deleted!", "Lead has been deleted.", "success");
              this.$router.go(-1);
            }
          });
        }
      });
    },
    getLead() {
      const { id } = this.$route.params;
      this.lead = null
      return applicationService.getById(id).then((response) => {
        this.lead = response.data;
        this.note = this.lead.note;
        this.setApplicationIndex();
        document.title = this.lead.fatherName || "Application";
      });
    },
  },
};
</script>

<style scoped>
.application-profile-page {
  min-height: 100%;
  padding: 22px 36px 48px;
  background: #f6f8f6;
}

.profile-topbar {
  margin-bottom: 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.profile-topbar > .v-btn {
  padding-left: 4px !important;
  text-transform: none;
  letter-spacing: 0;
}

.profile-navigation {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #687568;
  font-size: 12px;
  font-weight: 650;
}

.profile-alert {
  border-radius: 10px;
}

.profile-hero {
  padding: 22px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  border: 1px solid #e1e8e1;
  border-radius: 13px !important;
}

.hero-main,
.hero-actions {
  display: flex;
  align-items: center;
  gap: 14px;
}

.hero-identity h1 {
  margin: 0;
  color: #172033;
  font-size: 24px;
  line-height: 1.2;
}

.hero-identity p {
  margin: 4px 0 0;
  color: #7a877d;
  font-size: 11px;
}

.page-eyebrow {
  margin-bottom: 4px;
  color: #43a047;
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.hero-actions .v-btn {
  height: 39px;
  border-radius: 8px;
  text-transform: none;
  letter-spacing: 0;
}

.profile-content {
  padding: 18px 0 0 !important;
}

.information-grid {
  margin-bottom: 16px;
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(260px, 0.65fr);
  gap: 12px;
}

.information-card,
.students-card {
  border: 1px solid #e1e8e1;
  border-radius: 11px !important;
  overflow: hidden;
}

.section-heading,
.students-heading {
  padding: 15px 17px;
  display: flex;
  align-items: center;
  gap: 11px;
}

.section-heading h2,
.students-heading h2 {
  margin: 0;
  color: #273247;
  font-size: 1rem;
}

.section-heading p,
.students-heading p {
  margin: 2px 0 0;
  color: #929d95;
  font-size: 0.72rem;
}

.contact-grid {
  padding: 0 17px 17px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.contact-person {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  background: #f7faf7;
  border-radius: 9px;
}

.contact-person strong {
  color: #273247;
}

.contact-person a {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #4f6955;
  font-size: 0.76rem;
  overflow-wrap: anywhere;
}

.contact-role,
.field-label {
  color: #7f8d83;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.address-button {
  margin: 0 17px 17px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  color: #273247;
  background: #f7faf7;
  border-radius: 9px;
  text-align: left;
}

.address-button span {
  color: #657168;
}

.address-button small {
  margin-top: 8px;
  color: #43a047;
}

.empty-value {
  margin: 0 20px 20px;
  color: #929d95;
}

.emergency-card {
  margin-bottom: 16px;
}

.students-heading {
  justify-content: space-between;
  border-bottom: 1px solid #edf0ed;
}

.application-student-id {
  color: #2e7d32;
  font-weight: 800;
}

.metadata-card {
  background: #f8faf8;
}

.comment-section {
  margin-top: 8px;
}

.student-dialog {
  border-radius: 14px !important;
  overflow: hidden;
}

.student-dialog-header {
  min-height: 76px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: move;
  user-select: none;
}

.dialog-title {
  color: #172033;
}

.student-dialog-body {
  padding: 22px 22px 16px !important;
}

.student-age-summary {
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #35603d;
  background: #f0f8f1;
  border-radius: 8px;
  font-size: 0.78rem;
}

.student-age-summary--warning {
  color: #805e19;
  background: #fff8e8;
}

.student-dialog-actions {
  padding: 14px 20px !important;
  justify-content: flex-end;
  gap: 8px;
}

.student-dialog-actions .v-btn {
  border-radius: 8px;
  text-transform: none;
  letter-spacing: 0;
}

@media (max-width: 900px) {
  .application-profile-page {
    padding: 14px 14px 36px;
  }

  .profile-hero {
    align-items: flex-start;
    flex-direction: column;
  }

  .hero-actions {
    width: 100%;
  }

  .information-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .profile-topbar {
    grid-template-columns: 1fr auto;
  }

  .profile-navigation {
    grid-column: 1 / -1;
    grid-row: 2;
    justify-self: center;
  }

  .hero-main {
    align-items: flex-start;
  }

  .hero-main .v-avatar {
    display: none;
  }

  .hero-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .contact-grid {
    grid-template-columns: 1fr;
  }

  .students-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .drag-indicator {
    display: none;
  }
}
</style>
